import { MenuItem } from "@/app/ui/navigation/types";
import type { Location } from "vue-router";
import { dutySchemeType } from "@/components/pages/duty/composables/dutyConfiguration";
import type Guid from "@/app/types/common/guid";
import { EnumFeatureFlag } from "@/app/services/generated/enums/enumFeatureFlag.generated";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";

export const dutyMenuUs = {
  root: "navigation_duty_group_us",
  smallBrewersReportList: "us_small_brewers_report_list",
  smallBrewersReportListLocation: (): Location => ({
    name: dutyMenuUs.smallBrewersReportList,
  }),
  editReport: "us_small_brewers_report_edit",
  editReportLocation: (id: Guid, isEditing: boolean): Location => ({
    name: dutyMenuUs.editReport,
    params: {
      id: id.toString(),
      mode: isEditing ? "edit" : "view",
    },
  }),
  createReport: "us_small_brewers_report_create",
  createReportLocation: (): Location => ({
    name: dutyMenuUs.createReport,
  }),
  liquidProductionAndHoldingsReport: "us_liquid_production_and_holdings_report",
  liquidProductionAndHoldingsReportLocation: (): Location => ({
    name: dutyMenuUs.liquidProductionAndHoldingsReport,
  }),
  stockAudit: "us_stock_audit",
  stockAuditLocation: (): Location => ({
    name: dutyMenuUs.stockAudit,
  }),

  illinois: {
    root: "illinois_menu_item",
    taxReport: {
      view: "illinois_tax_report",
      viewLocation: (): Location => ({
        name: dutyMenuUs.illinois.taxReport.view,
      }),
    },
  },

  regionalTax: "regional_tax_audit",
};

export const buildDutyMenuUsItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: dutyMenuUs.root,
    icon: "Assignment",
    visible: () =>
      dutySchemeType.value.isUs() &&
      hasPermissionsUsingUserStore([EnumUserPermission.menuDutyView]),
    subMenus: [
      MenuItem.fromOpts({
        name: dutyMenuUs.smallBrewersReportList,
        url: "/duty/us/list",
        breadcrumbParentName: dutyMenuUs.root,
        view: () =>
          import(
            /* webpackChunkName: "us-duty" */ "@/components/pages/duty/us/ListUsSmallBrewersReport.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuUs.editReport,
        url: "/duty/us/:id/:mode",
        breadcrumbParentName: dutyMenuUs.smallBrewersReportList,
        view: () =>
          import(
            /* webpackChunkName: "us-duty" */ "@/components/pages/duty/us/EditUsSmallBrewersReport.vue"
          ),
        visible: false,
        componentProps: (route) => {
          return {
            id: route.params.id,
            editing:
              route.params.id &&
              route.params.mode.toLowerCase().trim() === "edit",
          };
        },
      }),
      MenuItem.fromOpts({
        name: dutyMenuUs.createReport,
        url: "/duty/us/create",
        breadcrumbParentName: dutyMenuUs.smallBrewersReportList,
        view: () =>
          import(
            /* webpackChunkName: "us-duty" */ "@/components/pages/duty/us/EditUsSmallBrewersReport.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          editing: true,
        }),
      }),
      MenuItem.fromOpts({
        name: dutyMenuUs.liquidProductionAndHoldingsReport,
        url: "/duty/us/liquidholdingreport",
        breadcrumbParentName: dutyMenuUs.root,
        view: () =>
          import(
            /* webpackChunkName: "us-duty" */ "@/components/pages/duty/us/LiquidProductionAndHoldingsReport.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuUs.stockAudit,
        url: "/duty/us/stockaudit",
        breadcrumbParentName: dutyMenuUs.root,
        view: () =>
          import(
            /* webpackChunkName: "us-duty" */ "@/components/pages/duty/us/StockAuditReport.vue"
          ),
      }),
      MenuItem.forBranch({
        name: dutyMenuUs.illinois.root,
        subMenus: [
          MenuItem.fromOpts({
            name: dutyMenuUs.illinois.taxReport.view,
            url: "/duty/us/illinois-tax-report",
            breadcrumbParentName: dutyMenuUs.illinois.root,
            view: () =>
              import(
                /* webpackChunkName: us-duty */ "@/components/pages/duty/us/illinois/IllinoisTaxReport.vue"
              ),
          }),
        ],
        requiredFeatureFlag: EnumFeatureFlag.showIllinoisTaxReturn,
      }),
      MenuItem.fromOpts({
        name: dutyMenuUs.regionalTax,
        url: "/duty/us/regional-tax-audit",
        breadcrumbParentName: dutyMenuUs.root,
        view: () =>
          import(
            /* webpackChunkName: "us-duty" */ "@/pages/duty/RegionalTaxAudit.vue"
          ),
      }),
    ],
  }),
];
